import PropTypes from 'prop-types';
import React from 'react';
import { Box, Card, Tab, Tabs, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/**
 * Sekme şeridi.
 *
 * Geniş ekranda sekmeler `fullWidth` ile alanı eşit paylaşır; içeriğe göre
 * daralıp sağda boşluk bırakmaz. Dar ekranda kaydırmaya döner, çünkü yedi
 * sekmeyi eşit bölmek okunmaz genişlikler üretirdi.
 *
 * Seçili sekmenin yazısı koyu kalır, vurgu turuncusu alt çizgide ve yumuşak
 * zeminde durur: temanın primary.contrastText'i primary.main ile aynı turuncu
 * olduğu için turuncu yazı turuncu zeminde okunmazdı.
 */
const TabBar = ({ tabs, value, onChange }) => {
  const theme = useTheme();
  const dar = useMediaQuery(theme.breakpoints.down('lg'));
  const koyu = theme.palette.mode === 'dark';

  const softAccent = koyu ? 'rgba(255,167,38,.14)' : '#FFF3E0';
  const hoverAccent = koyu ? 'rgba(255,255,255,.04)' : 'rgba(0,0,0,.03)';

  return (
    <Card sx={{ mb: 2, overflow: 'hidden' }}>
      <Tabs
        value={value}
        onChange={(e, v) => onChange(v)}
        variant={dar ? 'scrollable' : 'fullWidth'}
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          minHeight: 60,
          '& .MuiTabs-indicator': {
            height: 3,
            borderRadius: '3px 3px 0 0',
            backgroundColor: theme.palette.primary.main
          },
          '& .MuiTab-root': {
            minHeight: 60,
            textTransform: 'none',
            fontSize: '0.875rem',
            fontWeight: 500,
            color: theme.palette.text.secondary,
            borderRight: `1px solid ${theme.palette.divider}`,
            transition: 'background-color .15s ease, color .15s ease',
            '&:last-of-type': { borderRight: 0 },
            '&:hover': {
              backgroundColor: hoverAccent,
              color: theme.palette.text.primary
            },
            '&.Mui-selected': {
              color: theme.palette.text.primary,
              fontWeight: 600,
              backgroundColor: softAccent
            }
          },
          // İkon yazının rengini miras alır: seçili sekmede zemin açık turuncu
          // olduğu için turuncu ikon okunmuyordu. Vurgu turuncusu alt çizgide.
          '& .MuiTab-iconWrapper': { color: 'inherit', opacity: 0.8 }
        }}
      >
        {tabs.map((tab) => (
          <Tab
            key={tab.key}
            icon={tab.icon}
            iconPosition="start"
            label={
              <Box sx={{ textAlign: 'left', lineHeight: 1.25 }}>
                <Box component="span" sx={{ display: 'block' }}>
                  {tab.label}
                </Box>
                {tab.hint && (
                  <Typography
                    component="span"
                    sx={{
                      display: 'block',
                      fontSize: '0.68rem',
                      fontWeight: 400,
                      color: 'text.secondary'
                    }}
                  >
                    {tab.hint}
                  </Typography>
                )}
              </Box>
            }
          />
        ))}
      </Tabs>
    </Card>
  );
};

TabBar.propTypes = {
  tabs: PropTypes.array,
  value: PropTypes.number,
  onChange: PropTypes.func
};

export default TabBar;
